import { supabase } from '../../lib/supabase';

/**
 * Phase 179b — "what does my institute need from me", in one list.
 *
 * The mirror of `institute_my_work`. Nothing here is stored: `student_my_todo`
 * unions open assignments, live document requests and open tasks at read time,
 * so no status is duplicated and none of them can drift apart
 * (docs/MESSAGING_PLAN.md D3).
 *
 * `src/types/database.ts` predates these functions, so the shapes below are
 * the contract until it is regenerated.
 */
interface UnknownRpc {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>;
}
const rpcUnknown = supabase as unknown as UnknownRpc;

async function call<T>(fn: string, args: Record<string, unknown>, fallback: T): Promise<T> {
  const { data, error } = await rpcUnknown.rpc(fn, args);
  if (error !== null) throw new Error(error.message);
  return (data ?? fallback) as T;
}

export type TodoKind = 'assignment' | 'document' | 'task';

/**
 * Who closes this line (phase 180). `none` for an assignment or a document —
 * those close by being done, not by being ticked.
 */
export type TodoPolicy = 'none' | 'student_signals' | 'student_closes';

/**
 * Where the item is in its loop. This is the field that was missing: without
 * it a line the student CLOSES and a line they merely REPORT render as the
 * same checkbox, and the claim/confirm loop is invisible to the only person
 * standing in it.
 *
 *   todo      — not done
 *   sent_back — they marked it done and staff asked again
 *   checking  — reported / uploaded, waiting on the institute
 *   closed    — finished, by them
 */
export type TodoState = 'todo' | 'sent_back' | 'checking' | 'closed';

export interface TodoItem {
  kind: TodoKind;
  id: string;
  title: string;
  subtitle: string;
  due_at: string | null;
  /**
   * Only a task can be closed from here. An assignment is closed by taking the
   * test and a document request by sending the file — a tick on either would
   * be a lie about what the button does.
   */
  can_tick: boolean;
  done: boolean;
  policy: TodoPolicy;
  state: TodoState;
  href: string;
}

export const TODO_KEYS = {
  list: ['student', 'todo'] as const,
};

export async function listMyTodo(): Promise<TodoItem[]> {
  return call<TodoItem[]>('student_my_todo', {}, []);
}

export interface TaskTickResult {
  ok: boolean;
  policy: TodoPolicy;
  /** True when the tick actually closed the institute's own checklist line. */
  closed: boolean;
}

/**
 * The student's tick — which does one of two things, on purpose (phase 180).
 *
 * `student_closes`: it closes the institute's checklist line for real, and
 * counts toward auto-advance like any other completion.
 * `student_signals`: it is a CLAIM. The line stays open until staff confirm,
 * and nothing a student does writes to a workflow table.
 */
export async function setMyTaskDone(taskId: string, done: boolean): Promise<TaskTickResult> {
  return call<TaskTickResult>(
    'student_set_task_done',
    { p_task_id: taskId, p_done: done },
    { ok: true, policy: 'student_closes', closed: done },
  );
}
